import { useEffect, useState, useCallback } from "react";
import { api, dateFmt, formatApiError } from "@/lib/api";
import { Clock, Play, Square, Users, MapPin, Sun, Moon, RefreshCw, Calendar } from "lucide-react";
import { toast } from "sonner";

function getPosition() {
    return new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 6000, maximumAge: 30000 },
        );
    });
}

function fmtDuration(min) {
    if (min === null || min === undefined) return "—";
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}h ${m}m`;
}

export default function Attendance() {
    const [personnel, setPersonnel] = useState([]);
    const [list, setList] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filterPerson, setFilterPerson] = useState("");
    const [onlyOpen, setOnlyOpen] = useState(false);

    // Check-in form state
    const [form, setForm] = useState({ personnel_id: "", shift: "diurno", base: "", unit_id: "" });
    const [units, setUnits] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [people, attRes, sumRes, unitsRes] = await Promise.all([
                api.get("/personnel"),
                api.get(`/attendance?limit=200${onlyOpen ? "&only_open=true" : ""}${filterPerson ? `&personnel_id=${filterPerson}` : ""}`),
                api.get("/attendance/summary?days=7"),
                api.get("/units"),
            ]);
            setPersonnel(people.data || []);
            setList(attRes.data || []);
            setSummary(sumRes.data);
            setUnits(unitsRes.data || []);
        } catch (e) {
            console.warn(e);
        } finally {
            setLoading(false);
        }
    }, [filterPerson, onlyOpen]);

    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.personnel_id) {
            toast.error("Selecciona personal");
            return;
        }
        const pos = await getPosition();
        try {
            const { data } = await api.post("/attendance/checkin", {
                personnel_id: form.personnel_id,
                shift: form.shift,
                base: form.base || null,
                unit_id: form.unit_id || null,
                lat: pos?.lat ?? null,
                lng: pos?.lng ?? null,
            });
            toast.success("Check-in registrado");
            const linked = data?.auto_linked_services || [];
            if (linked.length > 0) {
                toast.info(`🔗 Vinculado automáticamente a ${linked.length} servicio(s) abierto(s): ${linked.map(l => l.folio).join(", ")}`);
            }
            setForm({ personnel_id: "", shift: "diurno", base: "", unit_id: "" });
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const doCheckout = async (rec) => {
        const pos = await getPosition();
        try {
            const { data } = await api.post(`/attendance/checkout/${rec.id}`, {
                lat: pos?.lat ?? null,
                lng: pos?.lng ?? null,
            });
            toast.success(`Check-out · ${fmtDuration(data.duration_minutes)}`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-4" data-testid="attendance-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
                        <Clock className="w-6 h-6 text-red-600" /> Asistencia y Turnos
                    </h2>
                    <div className="text-xs text-slate-500 font-mono">Check-in geo-localizado del personal</div>
                </div>
                <button onClick={load} disabled={loading} className="fm-btn-outline text-xs">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Actualizar
                </button>
            </div>

            {/* Summary KPIs */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="attendance-kpis">
                    <div className="fm-card p-4 border-l-4 border-l-emerald-500">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">En turno ahora</div>
                        <div className="font-mono font-black text-3xl text-emerald-600">{summary.open_today}</div>
                    </div>
                    <div className="fm-card p-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Cerrados hoy</div>
                        <div className="font-mono font-black text-3xl">{summary.closed_today}</div>
                    </div>
                    <div className="fm-card p-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Horas (7 días)</div>
                        <div className="font-mono font-black text-3xl">{summary.total_hours}h</div>
                    </div>
                    <div className="fm-card p-4">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Personas activas</div>
                        <div className="font-mono font-black text-3xl">{summary.unique_people}</div>
                    </div>
                </div>
            )}

            {/* Check-in form */}
            <form onSubmit={submit} className="fm-card p-5" data-testid="checkin-form">
                <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                    <Play className="w-4 h-4 text-emerald-600" /> Registrar entrada
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div>
                        <label className="fm-label">Personal</label>
                        <select
                            value={form.personnel_id}
                            onChange={(e) => setForm({ ...form, personnel_id: e.target.value })}
                            className="fm-input"
                            data-testid="checkin-personnel-select"
                        >
                            <option value="">— Seleccionar —</option>
                            {personnel.map((p) => (
                                <option key={p.id} value={p.id}>{p.name} · {p.position}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="fm-label">Turno</label>
                        <select
                            value={form.shift}
                            onChange={(e) => setForm({ ...form, shift: e.target.value })}
                            className="fm-input"
                            data-testid="checkin-shift-select"
                        >
                            <option value="diurno">Diurno (06-18)</option>
                            <option value="nocturno">Nocturno (18-06)</option>
                            <option value="partido">Partido</option>
                            <option value="guardia">Guardia 24h</option>
                        </select>
                    </div>
                    <div>
                        <label className="fm-label">Base / Punto</label>
                        <input
                            value={form.base}
                            onChange={(e) => setForm({ ...form, base: e.target.value })}
                            placeholder="Base CDMX, Polanco..."
                            className="fm-input"
                            data-testid="checkin-base-input"
                        />
                    </div>
                    <div>
                        <label className="fm-label">Unidad asignada (opcional)</label>
                        <select
                            value={form.unit_id}
                            onChange={(e) => setForm({ ...form, unit_id: e.target.value })}
                            className="fm-input"
                            data-testid="checkin-unit-select"
                            title="Si seleccionas una unidad, el sistema te asigna automáticamente a los servicios abiertos de esa ambulancia."
                        >
                            <option value="">— sin unidad —</option>
                            {units.filter((u) => u.status !== "fuera_servicio").map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.economic_number} · {u.plates} {u.type ? `(${u.type})` : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button type="submit" className="fm-btn-accent w-full" data-testid="checkin-submit-btn">
                            <Play className="w-3.5 h-3.5" /> Check-in
                        </button>
                    </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-3 font-mono flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Tu ubicación se captura automáticamente al hacer check-in
                </div>
            </form>

            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
                <select
                    value={filterPerson}
                    onChange={(e) => setFilterPerson(e.target.value)}
                    className="fm-input text-xs max-w-xs"
                    data-testid="filter-person-select"
                >
                    <option value="">Todo el personal</option>
                    {personnel.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
                <label className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-600 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={onlyOpen}
                        onChange={(e) => setOnlyOpen(e.target.checked)}
                        data-testid="filter-only-open"
                    />
                    Solo turnos abiertos
                </label>
            </div>

            {/* List */}
            <div className="fm-card overflow-hidden" data-testid="attendance-list">
                {list.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-500">
                        <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                        Sin registros de asistencia.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                {["Personal", "Posición", "Turno", "Fecha", "Entrada", "Salida", "Duración", "Base", "Acción"].map((h) => (
                                    <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((rec) => {
                                const open = !rec.check_out_at;
                                const ShiftIcon = rec.shift === "nocturno" ? Moon : Sun;
                                return (
                                    <tr key={rec.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`attendance-row-${rec.id}`}>
                                        <td className="py-2 px-3 font-medium">{rec.personnel_name}</td>
                                        <td className="py-2 px-3 text-xs text-slate-500">{rec.position}</td>
                                        <td className="py-2 px-3 text-xs">
                                            <span className="inline-flex items-center gap-1 uppercase font-bold tracking-wider">
                                                <ShiftIcon className="w-3 h-3" /> {rec.shift}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 font-mono text-xs">{rec.date}</td>
                                        <td className="py-2 px-3 font-mono text-xs">
                                            {dateFmt(rec.check_in_at).slice(11, 16)}
                                            {rec.check_in_lat && (
                                                <a
                                                    href={`https://www.google.com/maps?q=${rec.check_in_lat},${rec.check_in_lng}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="ml-1 text-emerald-600 hover:text-emerald-800"
                                                    title="Ver en mapa"
                                                >
                                                    <MapPin className="w-3 h-3 inline" />
                                                </a>
                                            )}
                                        </td>
                                        <td className="py-2 px-3 font-mono text-xs">
                                            {rec.check_out_at ? dateFmt(rec.check_out_at).slice(11, 16) : <span className="text-emerald-600 font-bold">EN TURNO</span>}
                                        </td>
                                        <td className="py-2 px-3 font-mono text-xs font-bold">{fmtDuration(rec.duration_minutes)}</td>
                                        <td className="py-2 px-3 text-xs text-slate-500">{rec.base || "—"}</td>
                                        <td className="py-2 px-3">
                                            {open && (
                                                <button
                                                    type="button"
                                                    onClick={() => doCheckout(rec)}
                                                    className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1 rounded-sm font-bold text-[10px] uppercase tracking-wider inline-flex items-center gap-1"
                                                    data-testid={`checkout-btn-${rec.id}`}
                                                >
                                                    <Square className="w-3 h-3" /> Check-out
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
